import React, { useEffect, useState } from 'react';
// @ts-ignore
import styles from '../Components/EPrescriptionMedicamentsWrapper.module.scss';
import AddButton from '../../../Components/Button/AddButton';
import Button from '../../../Components/Button/Button';
import { Theme } from '../../../Enums/Theme.enum';
import useTranslation from '../../../Hooks/useTranslation';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import EPrescriptionMedicamentsList from './EPrescriptionMedicamentsList';
import { EPrescriptionHVLPMedicament } from './EPrescriptionMedicamentsHVLPForm';
import { EPrescriptionIPLPMedicament } from './EPrescriptionMedicamentsIPLPForm';
import EPrescriptionMedicamentsModal, {
	MedicamentsForm,
} from './EPrescriptionMedicamentsModal';
import { Size } from '../../../Enums/Size.enum';
import Text from '../../../Components/Base/Text/Text';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import { useEffectAsync } from '../../../helpers';
import { TreatmentFormModalContextKeys } from '../../../Components/Treatment/TreatmentFormModalContextKeys';
import { useModalContext } from '../../Modal/Services/ModalContext';
import { Values } from './EPrescriptionWrapper';

interface EPrescriptionMedicamentsWrapperProps {
	patientId: string;
	onMedicamentsUpdate?: (values: EPrescriptionMedicament[]) => void;
	onSuccess?: (params?: any) => void;
	defaultValues?: {
		hvlp?: EPrescriptionHVLPMedicament[];
		iplp?: EPrescriptionIPLPMedicament[];
	};
	formValues?: Values;
}

export type EPrescriptionMedicament =
	| EPrescriptionHVLPMedicament
	| EPrescriptionIPLPMedicament;

const EPrescriptionMedicamentsWrapper: React.FC<
	EPrescriptionMedicamentsWrapperProps
> = ({
	patientId,
	defaultValues,
	onMedicamentsUpdate,
	onSuccess,
	formValues,
}) => {
	const { t } = useTranslation();
	const [medicamentFormDefaults, setMedicamentFormDefaults] =
		useState<EPrescriptionMedicament>();
	const [areModalsOpen, setAreModalsOpen] = useState({
		HVLP: false,
		IPLP: false,
	});
	const { registerFormByKey } = useModalContext();

	const [prescriptions, setPrescriptions] = useState<EPrescriptionMedicament[]>(
		[],
	);

	useEffect(() => {
		if (defaultValues) {
			setPrescriptions([...defaultValues?.hvlp, ...defaultValues?.iplp]);
		}
	}, []);

	useEffectAsync(async () => {
		onMedicamentsUpdate?.(prescriptions);
	}, [prescriptions]);

	const handleHVLPFormClick = () => {
		setMedicamentFormDefaults(undefined);
		setAreModalsOpen(({ IPLP }) => ({
			HVLP: true,
			IPLP,
		}));
	};

	const handleIPLPFormClick = () => {
		setMedicamentFormDefaults(undefined);
		setAreModalsOpen(({ HVLP }) => ({
			HVLP,
			IPLP: true,
		}));
	};

	const handleClose = () => {
		setAreModalsOpen(prev => ({ ...prev, HVLP: false, IPLP: false }));
	};

	const handleSubmit = (
		values: EPrescriptionHVLPMedicament | EPrescriptionIPLPMedicament,
	) => {
		const prev = prescriptions.filter(({ id }) => id != values.id);
		setPrescriptions([...prev, values]);
	};

	const handleRemoveMedicament = (item: EPrescriptionMedicament) => {
		setPrescriptions(prescriptions.filter(medicament => medicament != item));
	};

	const handleEditMedicamentItem = (item: EPrescriptionMedicament) => {
		setMedicamentFormDefaults(item);
		if (item.formType === PrescriptionType.HVLP) {
			setAreModalsOpen(({ IPLP }) => ({
				HVLP: true,
				IPLP,
			}));
		}
		if (item.formType === PrescriptionType.IPLP) {
			setAreModalsOpen(({ HVLP }) => ({
				HVLP,
				IPLP: true,
			}));
		}
	};

	useEffect(() => {
		registerFormByKey(
			TreatmentFormModalContextKeys.PRESCRIPTION_STEP_2_MEDICAMENT_LIST,
		);
	}, []);

	return (
		<div className={styles.wrapper}>
			<EPrescriptionMedicamentsModal
				isOpen={areModalsOpen.HVLP || areModalsOpen.IPLP}
				form={areModalsOpen.HVLP ? MedicamentsForm.HVLP : MedicamentsForm.IPLP}
				modalTitle={
					areModalsOpen.HVLP
						? t('component.ePrescription.addItemHVLP') || ''
						: t('component.ePrescription.addItemIPLP') || ''
				}
				size={Size.SM}
				onClose={handleClose}
				onSubmit={handleSubmit}
				defaultValues={medicamentFormDefaults}
				patientId={patientId}
				isSelfPayer={
					formValues?.patientInformation?.insuranceCompany?.value === '999'
				}
			/>
			<Text tx={'base.meds'} className={styles.title} firstUpper />
			<div className={styles.options}>
				<AddButton
					title='component.ePrescription.addHVLP'
					onClick={handleHVLPFormClick}
				/>
				<AddButton
					title='component.ePrescription.addIPLP'
					onClick={handleIPLPFormClick}
				/>
			</div>
			<ScrollContainer>
				<EPrescriptionMedicamentsList
					medicaments={prescriptions}
					onRemove={handleRemoveMedicament}
					onEdit={handleEditMedicamentItem}
				/>
				<div className='d-flex mt-4 justify-content-between pr-3'>
					<Button
						theme={Theme.GREEN}
						title='base.shared.continue'
						className={styles.submitButton}
						onClick={() => onSuccess?.()}
					/>
				</div>
			</ScrollContainer>
		</div>
	);
};

export default EPrescriptionMedicamentsWrapper;
